import { Button } from "@capacit-com/lara-law-shared-ui";
import { useUploadHub } from "@/pages/workspace/hooks/useUploadHub";
import { useWorkspaceContext } from "@/pages/workspace/providers/WorkspaceContext";
import { FolderOpen, FolderPlus, Link2, SearchX, Upload } from "lucide-react";
import { FC } from "react";

type EmptyStateProps = {
  currentPath: { id: string; name: string }[];
  canUserUploadFiles: () => boolean;
  setIsCreateFolderDialogOpen: (open: boolean) => void;
  setIsAddLinkDialogOpen: (open: boolean) => void;
};

const EmptyState: FC<EmptyStateProps> = ({
  currentPath,
  canUserUploadFiles,
  setIsCreateFolderDialogOpen,
  setIsAddLinkDialogOpen,
}) => {
  const {
    search,
    setSearch,
    selectedMetadataFilters,
    setSelectedMetadataFilters,
  } = useWorkspaceContext();
  const { onUploadFilesFromComputer } = useUploadHub();

  return (
    <div className="flex flex-col grow h-full">
      <div className="flex flex-1 flex-col items-center justify-center rounded-md border border-dashed p-8 text-center h-full w-full">
        {search.searchString.trim() !== "" ||
        selectedMetadataFilters.metadataNames.length > 0 ? (
          <>
            <SearchX className="mb-4 h-12 w-12 text-muted-foreground" />
            <h3 className="mb-2 text-lg font-medium">Ingen resultater</h3>
            <p className="mb-4 text-sm text-muted-foreground">
              {search.searchString.trim() !== "" &&
              selectedMetadataFilters.metadataNames.length > 0
                ? `Ingen elementer matcher din søgning "${
                    search.searchString
                  }" og metadata filtre: ${selectedMetadataFilters.metadataNames.join(
                    ", "
                  )}. Prøv at ændre søgeord eller filtre.`
                : search.searchString.trim() !== ""
                  ? `Ingen elementer matcher din søgning "${search.searchString}". Prøv et andet søgeord.`
                  : `Ingen elementer matcher dine filtre: ${selectedMetadataFilters.metadataNames.join(
                      ", "
                    )}. Prøv at ændre filtrene.`}
            </p>
            <div className="flex gap-2">
              {search.searchString.trim() !== "" && (
                <Button
                  variant="outline"
                  onClick={() =>
                    setSearch((prev) => ({ ...prev, searchString: "" }))
                  }
                >
                  Ryd søgning
                </Button>
              )}
              {selectedMetadataFilters.metadataNames.length > 0 && (
                <Button
                  variant="outline"
                  onClick={() =>
                    setSelectedMetadataFilters({
                      ...selectedMetadataFilters,
                      metadataNames: [],
                    })
                  }
                >
                  Ryd metadata filtre
                </Button>
              )}
            </div>
          </>
        ) : (
          <>
            <FolderOpen className="mb-4 h-12 w-12 text-muted-foreground" />
            <h3 className="mb-2 text-lg font-medium">Denne mappe er tom</h3>
            <p className="mb-4 text-sm text-muted-foreground">
              {currentPath.length > 0
                ? `Der er ingen filer, mapper eller links i "${
                    currentPath[currentPath.length - 1].name
                  }".`
                : "Der er ingen filer, mapper eller links i dette workspace."}
            </p>
            <div className="flex gap-2">
              <Button
                onClick={onUploadFilesFromComputer}
                disabled={!canUserUploadFiles()}
              >
                <Upload className="mr-2 h-4 w-4" />
                Upload filer
              </Button>
              <Button
                variant="outline"
                onClick={() => setIsCreateFolderDialogOpen(true)}
                disabled={!canUserUploadFiles()}
              >
                <FolderPlus className="mr-2 h-4 w-4" />
                Opret mappe
              </Button>
              <Button
                variant="outline"
                onClick={() => setIsAddLinkDialogOpen(true)}
                disabled={!canUserUploadFiles()}
              >
                <Link2 className="mr-2 h-4 w-4" />
                Tilføj link
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default EmptyState;
